import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

export const PRE_ISSUES = [
  "UC Not Done",
  "2nd Tranche Rejected",
  "3rd Tranche Rejected",
  "ATL Dashboard Not Filled",
  "User Doesn't Know Anything",
];
const FINANCIAL_YEARS = ["FY 2020-21", "FY 2021-22", "FY 2022-23", "FY 2023-24", "FY 2024-25", "FY 2025-26"];
const TRANCHE_OPTIONS = ["2nd Tranche", "3rd Tranche", "Both 2nd & 3rd"];
const SCHOOL_STATUSES = ["Pending For Review", "Approved"];
export const STAGES = ["Stage 1 — Drafting", "Stage 2 — Uploading", "Stage 3 — Review", "Stage 4 — Funded"];

const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};

function emptyStatus() {
  return {
    pre_issues: [],
    current_stage: "",
    // Stage 1 — ATL 1.0
    s1_uc_capital_drafted: false,
    s1_uc_operational_drafted: false,
    s1_audited_statement_drafted: false,
    s1_eat_report_drafted: false,
    s1_school_contact_info_drafted: false,
    s1_supplementary_mou_drafted: false,
    s1_atl_video_received: false,
    s1_atl_video_url: "",
    // Stage 2 — ATL 1.0
    s2_docs_uploaded: false,
    s2_uc_uploaded: false,
    s2_uc_financial_years: [],
    s2_tranche_uploaded: "",
    s2_atl_dashboard_updated: false,
    // Stage 3 — ATL 1.0
    s3_school_status: "",
    // Stage 4 — ATL 1.0
    s4_fund_credited: false,
    s4_fund_credited_amount: "",
    s4_fund_credited_date: "",
    // ATL 2.0 specific
    atl2_current_stage: "",
    atl2_google_form_filled: false,
    atl2_portal_data_filled: false,
    atl2_application_code: "",
    atl2_application_status: "",
  };
}

export const ATL2_STAGES = [
  "Stage 1 — Google Form",
  "Stage 2 — Portal Data",
  "Stage 3 — Application Status",
];
const ATL2_INDEX = { "": -1, "Stage 1 — Google Form": 0, "Stage 2 — Portal Data": 1, "Stage 3 — Application Status": 2 };
const ATL2_APP_STATUSES = ["Approved", "Rejected", "Pending"];

const STAGE_INDEX = { "": -1, "Stage 1 — Drafting": 0, "Stage 2 — Uploading": 1, "Stage 3 — Review": 2, "Stage 4 — Funded": 3 };

export default function DocStatusDialog({ open, onOpenChange, client, readOnly = false, onSaved }) {
  const [form, setForm] = useState(emptyStatus());
  const [orig, setOrig] = useState(emptyStatus());
  const [logs, setLogs] = useState([]);
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  // Detect ATL 2.0 client to switch UI
  const isATL2 = useMemo(() => {
    const t = (client?.client_type || "").trim();
    const ts = client?.client_types || [];
    return t === "ATL 2.0" || ts.includes("ATL 2.0");
  }, [client?.client_type, client?.client_types]);

  useEffect(() => {
    if (open && client?.id) {
      api.get(`/crm/clients/${client.id}/doc-status`).then(({ data }) => {
        const merged = { ...emptyStatus(), ...(data.documentation_status || {}) };
        setForm(merged);
        setOrig(merged);
        setLogs(data.logs || []);
      }).catch(() => { setForm(emptyStatus()); setOrig(emptyStatus()); setLogs([]); });
      setRemark("");
    }
  }, [open, client?.id]);

  const currentIdx = STAGE_INDEX[form.current_stage] ?? -1;
  const maxUnlockedIdx = useMemo(() => {
    // Allow the picker to advance one step beyond current
    const origIdx = STAGE_INDEX[orig.current_stage] ?? -1;
    return Math.max(currentIdx, origIdx + 1);
  }, [orig.current_stage, currentIdx]);

  const isReadOnly = readOnly;

  const togglePre = (issue) => {
    if (isReadOnly) return;
    setForm((f) => {
      const set = new Set(f.pre_issues || []);
      if (set.has(issue)) set.delete(issue); else set.add(issue);
      return { ...f, pre_issues: Array.from(set) };
    });
  };

  const toggleFY = (fy) => {
    if (isReadOnly) return;
    setForm((f) => {
      const set = new Set(f.s2_uc_financial_years || []);
      if (set.has(fy)) set.delete(fy); else set.add(fy);
      return { ...f, s2_uc_financial_years: Array.from(set) };
    });
  };

  const submit = async () => {
    const needStage = isATL2 ? !form.atl2_current_stage : !form.current_stage;
    if (needStage) {
      toast.error("Pick the current stage of this school");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, __remark: remark };
      const { data } = await api.post(`/crm/clients/${client.id}/doc-status`, payload);
      if (!data.updated) {
        toast.info("No changes to save");
      } else {
        toast.success(`Saved · ${data.changes.length} field(s) logged`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const advanceStage = (nextStage, completeKey) => {
    setForm((f) => ({
      ...f,
      current_stage: nextStage,
      [completeKey]: true,
      [`${completeKey}_at`]: new Date().toISOString(),
    }));
    toast.info(`Stage marked complete → ${nextStage}. Click Save to record the change.`);
  };

  const advanceATL2Stage = (nextStage, completeKey) => {
    setForm((f) => ({
      ...f,
      atl2_current_stage: nextStage,
      [completeKey]: true,
      [`${completeKey}_at`]: new Date().toISOString(),
    }));
    toast.info(`Stage marked complete → ${nextStage}. Click Save to record the change.`);
  };

  const stageVisible = (idx) => idx <= Math.max(currentIdx, STAGE_INDEX[orig.current_stage] ?? -1);
  const stageSelectable = (idx) => idx <= maxUnlockedIdx;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="doc-status-dialog">
        <DialogHeader>
          <DialogTitle className="heading">
            {isReadOnly ? "School Status — " : "Update School Stage — "}{client?.client_name}
          </DialogTitle>
          <div className="text-[11px] mono text-slate-500">U-DISE · {client?.u_dise || "—"}</div>
        </DialogHeader>

        <div className="py-2 space-y-5">
          {/* Pre-stage issues — visible always */}
          <div className="border border-orange-200 bg-orange-50/40 rounded-md p-4">
            <div className="pk-table-header mb-2 text-orange-700">Previous Issues (when client was assigned)</div>
            <div className="text-[11px] text-slate-600 mb-2">Select all that apply — these stay attached to the school for context.</div>
            <div className="flex flex-wrap gap-1.5" data-testid="doc-pre-issues">
              {PRE_ISSUES.map((iss) => {
                const active = (form.pre_issues || []).includes(iss);
                return (
                  <button
                    key={iss}
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => togglePre(iss)}
                    className={`px-3 py-1.5 text-[12px] rounded-full border-2 transition-all ${active ? "bg-orange-600 border-orange-600 text-white font-semibold" : "bg-white border-orange-200 text-orange-700 hover:bg-orange-50"} ${isReadOnly ? "opacity-70 cursor-default" : ""}`}
                    data-testid={`doc-pre-${iss.replace(/[^A-Za-z0-9]/g, "_")}`}
                  >
                    {active && <span className="mr-1">✓</span>}{iss}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stage picker */}
          {!isATL2 && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Current Stage *</Label>
              <Select value={form.current_stage} onValueChange={(v) => setForm({ ...form, current_stage: v })} disabled={isReadOnly}>
                <SelectTrigger className="mt-1.5" data-testid="doc-stage"><SelectValue placeholder="Pick the current stage…" /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s, idx) => (
                    <SelectItem key={s} value={s} disabled={!stageSelectable(idx)}>{s}{!stageSelectable(idx) ? " · (unlock previous stage first)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-500 mt-1">
                You can only move forward one stage at a time. Previous stages stay editable below.
              </div>
            </div>
          )}

          {/* ATL 2.0 — different stage progression */}
          {isATL2 && (
            <ATL2StagePanel
              form={form}
              setForm={setForm}
              orig={orig}
              isReadOnly={isReadOnly}
              advance={advanceATL2Stage}
            />
          )}

          {/* Stage 1 — visible only after Stage 1 (or beyond) [ATL 1.0 only] */}
          {!isATL2 && stageVisible(0) && (
            <div className="border border-violet-200 bg-violet-50/40 rounded-md p-4">
              <div className="pk-table-header mb-3 text-violet-700">Stage 1 — Did we draft the Documents?</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <ToggleRow label="UC for Capital Fund drafted" checked={form.s1_uc_capital_drafted} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s1_uc_capital_drafted: v })} testid="doc-s1-uc-capital" />
                <ToggleRow label="UC for Operational Fund drafted" checked={form.s1_uc_operational_drafted} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s1_uc_operational_drafted: v })} testid="doc-s1-uc-operational" />
                <ToggleRow label="Audited Statement drafted" checked={form.s1_audited_statement_drafted} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s1_audited_statement_drafted: v })} testid="doc-s1-audited" />
                <ToggleRow label="EAT Report drafted" checked={form.s1_eat_report_drafted} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s1_eat_report_drafted: v })} testid="doc-s1-eat" />
                <ToggleRow label="School Contact Info collected" checked={form.s1_school_contact_info_drafted} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s1_school_contact_info_drafted: v })} testid="doc-s1-contact" />
                <ToggleRow label="Supplementary MOU drafted" checked={form.s1_supplementary_mou_drafted} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s1_supplementary_mou_drafted: v })} testid="doc-s1-mou" />
              </div>
              <div className="mt-3 border-t border-violet-200 pt-3">
                <div className="flex items-center gap-3">
                  <Switch disabled={isReadOnly} checked={form.s1_atl_video_received} onCheckedChange={(v) => setForm({ ...form, s1_atl_video_received: v, s1_atl_video_url: v ? form.s1_atl_video_url : "" })} data-testid="doc-s1-video" />
                  <span className="text-sm text-slate-800 font-medium">ATL Video Link shared by client?</span>
                </div>
                {form.s1_atl_video_received && (
                  <div className="mt-2">
                    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Video URL</Label>
                    <Input disabled={isReadOnly} value={form.s1_atl_video_url} onChange={(e) => setForm({ ...form, s1_atl_video_url: e.target.value })} placeholder="https://…" className="mt-1" data-testid="doc-s1-video-url" />
                  </div>
                )}
              </div>
              {!isReadOnly && currentIdx <= 0 && (
                <div className="mt-3 border-t border-violet-200 pt-3 flex justify-end">
                  <Button
                    type="button"
                    onClick={() => advanceStage("Stage 2 — Uploading", "s1_completed")}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs"
                    data-testid="doc-s1-mark-complete"
                  >
                    ✓ Mark Stage 1 Complete → Move to Stage 2
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Stage 2 [ATL 1.0 only] */}
          {!isATL2 && stageVisible(1) && (
            <div className="border border-blue-200 bg-blue-50/40 rounded-md p-4">
              <div className="pk-table-header mb-3 text-blue-700">Stage 2 — Did we upload the Documents?</div>
              <ToggleRow label="Documents uploaded?" checked={form.s2_docs_uploaded} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s2_docs_uploaded: v })} testid="doc-s2-uploaded" />
              <div className="mt-3 border-t border-blue-200 pt-3">
                <ToggleRow label="UC uploaded?" checked={form.s2_uc_uploaded} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s2_uc_uploaded: v, s2_uc_financial_years: v ? form.s2_uc_financial_years : [] })} testid="doc-s2-uc" />
                {form.s2_uc_uploaded && (
                  <div className="mt-2">
                    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">FY(s) UC uploaded for</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="doc-s2-fys">
                      {FINANCIAL_YEARS.map((fy) => {
                        const active = (form.s2_uc_financial_years || []).includes(fy);
                        return (
                          <button key={fy} type="button" disabled={isReadOnly} onClick={() => toggleFY(fy)}
                            className={`px-3 py-1.5 text-[12px] rounded-full border-2 transition-all ${active ? "bg-blue-600 border-blue-600 text-white font-semibold" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"} ${isReadOnly ? "opacity-70 cursor-default" : ""}`}>
                            {active && <span className="mr-1">✓</span>}{fy}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 border-t border-blue-200 pt-3">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Uploaded for which Tranche?</Label>
                <Select value={form.s2_tranche_uploaded} onValueChange={(v) => setForm({ ...form, s2_tranche_uploaded: v })} disabled={isReadOnly}>
                  <SelectTrigger className="mt-1.5" data-testid="doc-s2-tranche"><SelectValue placeholder="Select tranche…" /></SelectTrigger>
                  <SelectContent>{TRANCHE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="mt-3 border-t border-blue-200 pt-3">
                <ToggleRow label="ATL Dashboard updated?" checked={form.s2_atl_dashboard_updated} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s2_atl_dashboard_updated: v })} testid="doc-s2-dashboard" />
              </div>
              {!isReadOnly && currentIdx <= 1 && (
                <div className="mt-3 border-t border-blue-200 pt-3 flex justify-end">
                  <Button
                    type="button"
                    onClick={() => advanceStage("Stage 3 — Review", "s2_completed")}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs"
                    data-testid="doc-s2-mark-complete"
                  >
                    ✓ Mark Stage 2 Complete → Move to Stage 3
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Stage 3 [ATL 1.0 only] */}
          {!isATL2 && stageVisible(2) && (
            <div className="border border-amber-200 bg-amber-50/40 rounded-md p-4">
              <div className="pk-table-header mb-3 text-amber-700">Stage 3 — Status of School</div>
              <Select value={form.s3_school_status} onValueChange={(v) => setForm({ ...form, s3_school_status: v })} disabled={isReadOnly}>
                <SelectTrigger data-testid="doc-s3-status"><SelectValue placeholder="Pick status…" /></SelectTrigger>
                <SelectContent>{SCHOOL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {!isReadOnly && currentIdx <= 2 && (
                <div className="mt-3 border-t border-amber-200 pt-3 flex justify-end">
                  <Button
                    type="button"
                    onClick={() => advanceStage("Stage 4 — Funded", "s3_completed")}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs"
                    data-testid="doc-s3-mark-complete"
                  >
                    ✓ Mark Stage 3 Complete → Move to Stage 4
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Stage 4 [ATL 1.0 only] */}
          {!isATL2 && stageVisible(3) && (
            <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-4">
              <div className="pk-table-header mb-3 text-emerald-700">Stage 4 — Fund Credited</div>
              <ToggleRow label="Fund credited in school account?" checked={form.s4_fund_credited} disabled={isReadOnly} onChange={(v) => setForm({ ...form, s4_fund_credited: v })} testid="doc-s4-credited" />
              {form.s4_fund_credited && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 border-t border-emerald-200 pt-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Amount (INR)</Label>
                    <Input disabled={isReadOnly} value={form.s4_fund_credited_amount} onChange={(e) => setForm({ ...form, s4_fund_credited_amount: e.target.value })} placeholder="0" className="mt-1" data-testid="doc-s4-amount" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Date Credited</Label>
                    <Input disabled={isReadOnly} type="date" value={form.s4_fund_credited_date} onChange={(e) => setForm({ ...form, s4_fund_credited_date: e.target.value })} className="mt-1" data-testid="doc-s4-date" />
                  </div>
                </div>
              )}
            </div>
          )}

          {!isReadOnly && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Optional remark for this update</Label>
              <Textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} className="mt-1.5" placeholder="What did you update and why?" data-testid="doc-status-remark" />
            </div>
          )}

          {/* History */}
          <div>
            <div className="pk-table-header mb-2">Change History ({logs.length})</div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {logs.length === 0 && <div className="text-xs text-slate-400 italic text-center py-3 border border-dashed border-slate-200 rounded-md">No history yet.</div>}
              {logs.map((l) => (
                <div key={l.id} className="border border-slate-200 rounded-md p-2.5 text-[12px]" data-testid={`doc-status-log-${l.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {l.log_type && <Badge className={`text-[10px] ${l.log_type === "claim" ? "bg-slate-900 text-white" : "bg-violet-100 text-violet-700"} hover:opacity-90`}>{l.log_type === "claim" ? "Claimed" : "Updated"}</Badge>}
                      {l.current_stage && <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 text-[10px]">{l.current_stage}</Badge>}
                      <span className="text-[11px] text-slate-700">by <span className="font-semibold">{l.created_by_name}</span></span>
                    </div>
                    <span className="mono text-[10px] text-slate-400">{fmtDateTime(l.created_at)}</span>
                  </div>
                  {l.remark && <div className="text-slate-700 mt-1.5">"{l.remark}"</div>}
                  {(l.changes || []).length > 0 && (
                    <ul className="mt-1.5 ml-3 space-y-0.5">
                      {l.changes.map((c, i) => (
                        <li key={i} className="text-[11px] text-slate-600">
                          <span className="font-semibold mono">{c.field}</span>:{" "}
                          <span className="line-through text-slate-400">{formatVal(c.from)}</span>
                          <span className="mx-1">→</span>
                          <span className="text-slate-900 font-semibold">{formatVal(c.to)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {!isReadOnly && (
            <Button onClick={submit} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="doc-status-save">
              {saving ? "Saving…" : "Save Stage Update"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ label, checked, onChange, testid, disabled }) {
  return (
    <div className="flex items-center gap-3">
      <Switch disabled={disabled} checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
      <span className="text-sm text-slate-800">{label}</span>
    </div>
  );
}

function formatVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v);
}

function ATL2StagePanel({ form, setForm, orig, isReadOnly, advance }) {
  const currentIdx = ATL2_INDEX[form.atl2_current_stage] ?? -1;
  const origIdx = ATL2_INDEX[orig.atl2_current_stage] ?? -1;
  const maxIdx = Math.max(currentIdx, origIdx + 1);
  const visible = (idx) => idx <= Math.max(currentIdx, origIdx);
  const selectable = (idx) => idx <= maxIdx;

  return (
    <>
      <div>
        <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Current Stage (ATL 2.0) *</Label>
        <Select value={form.atl2_current_stage} onValueChange={(v) => setForm({ ...form, atl2_current_stage: v })} disabled={isReadOnly}>
          <SelectTrigger className="mt-1.5" data-testid="doc-atl2-stage"><SelectValue placeholder="Pick the current stage…" /></SelectTrigger>
          <SelectContent>
            {ATL2_STAGES.map((s, idx) => (
              <SelectItem key={s} value={s} disabled={!selectable(idx)}>{s}{!selectable(idx) ? " · (unlock previous stage first)" : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-[10px] text-slate-500 mt-1">ATL 2.0 schools follow: Google Form → Portal Data → Application Status.</div>
      </div>

      {/* Stage 1 — Google Form */}
      {visible(0) && (
        <div className="border border-violet-200 bg-violet-50/40 rounded-md p-4">
          <div className="pk-table-header mb-3 text-violet-700">Stage 1 — Google Form</div>
          <div className="flex items-center gap-3">
            <Switch disabled={isReadOnly} checked={!!form.atl2_google_form_filled} onCheckedChange={(v) => setForm({ ...form, atl2_google_form_filled: v })} data-testid="doc-atl2-form" />
            <span className="text-sm text-slate-800">Is Google Form filled?</span>
          </div>
          {!isReadOnly && currentIdx <= 0 && (
            <div className="mt-3 border-t border-violet-200 pt-3 flex justify-end">
              <Button
                type="button"
                onClick={() => advance("Stage 2 — Portal Data", "atl2_s1_completed")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs"
                data-testid="doc-atl2-s1-mark-complete"
              >
                ✓ Mark Stage 1 Complete → Move to Stage 2
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Stage 2 — Portal Data + Application Code */}
      {visible(1) && (
        <div className="border border-blue-200 bg-blue-50/40 rounded-md p-4">
          <div className="pk-table-header mb-3 text-blue-700">Stage 2 — Portal Data</div>
          <div className="flex items-center gap-3">
            <Switch disabled={isReadOnly} checked={!!form.atl2_portal_data_filled} onCheckedChange={(v) => setForm({ ...form, atl2_portal_data_filled: v, atl2_application_code: v ? form.atl2_application_code : "" })} data-testid="doc-atl2-portal" />
            <span className="text-sm text-slate-800">Is data filled on the Portal?</span>
          </div>
          {form.atl2_portal_data_filled && (
            <div className="mt-3 border-t border-blue-200 pt-3">
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Application Code</Label>
              <Input disabled={isReadOnly} value={form.atl2_application_code} onChange={(e) => setForm({ ...form, atl2_application_code: e.target.value })} placeholder="Eg. ATL2-APP-2026-001" className="mt-1.5" data-testid="doc-atl2-appcode" />
            </div>
          )}
          {!isReadOnly && currentIdx <= 1 && (
            <div className="mt-3 border-t border-blue-200 pt-3 flex justify-end">
              <Button
                type="button"
                onClick={() => advance("Stage 3 — Application Status", "atl2_s2_completed")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs"
                data-testid="doc-atl2-s2-mark-complete"
              >
                ✓ Mark Stage 2 Complete → Move to Stage 3
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Stage 3 — Application Status */}
      {visible(2) && (
        <div className="border border-amber-200 bg-amber-50/40 rounded-md p-4">
          <div className="pk-table-header mb-3 text-amber-700">Stage 3 — Application Status</div>
          <Select value={form.atl2_application_status} onValueChange={(v) => setForm({ ...form, atl2_application_status: v })} disabled={isReadOnly}>
            <SelectTrigger data-testid="doc-atl2-status"><SelectValue placeholder="Pick status…" /></SelectTrigger>
            <SelectContent>{ATL2_APP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {form.atl2_application_status === "Approved" && (
            <div className="mt-3 px-3 py-2 bg-rose-50 border border-rose-200 rounded-md text-[12px] text-rose-800">
              🔥 On save, this school will be flagged <span className="font-bold">HOT · Has Funds</span> and shown to the sales assignee in CRM Reporting.
            </div>
          )}
        </div>
      )}
    </>
  );
}
